import { useState } from 'react';
import { AlertTriangle, ShieldCheck, ShieldX, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '../../utils/timezone';
import sprayService from '../../services/spray';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { SprayApplication } from '../../types/spray';

// A spray held because its withholding period runs past a target block's
// harvest date.
//
// The creator raised it and wrote the justification; only a Spray Admin seat
// can clear it. That split is the feature: an employee planning the job cannot
// decide that picking inside a withholding period is acceptable. The seat check
// here only shows or hides the buttons — the server enforces it.

interface Props {
  spray: SprayApplication;
  onDecided: () => void;
}

export function WhpOverridePanel({ spray, onDecided }: Props) {
  const timezone = useOrgTimezone();
  const { hasAccess: isSprayAdmin, loading: licenseLoading } = useModuleLicense('spray', 'admin');
  const [notes, setNotes] = useState('');
  const [deciding, setDeciding] = useState<'approved' | 'rejected' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const status = spray.whp_override_status ?? 'none';
  if (status === 'none') return null;

  const conflicts = spray.whp_conflicts ?? [];

  const decide = async (decision: 'approved' | 'rejected') => {
    setDeciding(decision);
    setError(null);
    try {
      const res = await sprayService.decideWhpOverride(spray.id, decision, notes.trim() || undefined);
      if (res.success) {
        setNotes('');
        onDecided();
      } else {
        setError((res as any).error?.message || 'Failed to record the decision');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Failed to record the decision');
    } finally {
      setDeciding(null);
    }
  };

  const tone =
    status === 'pending'  ? 'border-red-300 bg-red-50'
    : status === 'rejected' ? 'border-red-300 bg-red-50'
    : 'border-amber-300 bg-amber-50';

  const heading =
    status === 'pending'  ? 'Held — awaiting a Spray Admin decision'
    : status === 'rejected' ? 'Rejected by a Spray Admin'
    : 'Withholding conflict approved';

  return (
    <div className={`rounded-xl border px-5 py-4 ${tone}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className={`h-5 w-5 mt-0.5 shrink-0 ${status === 'approved' ? 'text-amber-600' : 'text-red-600'}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${status === 'approved' ? 'text-amber-800' : 'text-red-800'}`}>
            {heading}
          </p>
          <p className={`text-sm mt-0.5 ${status === 'approved' ? 'text-amber-700' : 'text-red-700'}`}>
            The {spray.whp_days_applied}-day withholding period runs to{' '}
            {spray.whp_clear_date
              ? formatLocalDateInTz(spray.whp_clear_date, timezone)
              : '—'}
            , past the earliest harvest date on {conflicts.length} target block
            {conflicts.length !== 1 ? 's' : ''}.
          </p>

          {conflicts.length > 0 && (
            <ul className="mt-2 space-y-1">
              {conflicts.map((c) => (
                <li key={c.cultivar_area_id} className="text-sm text-red-700">
                  <span className="font-medium">
                    {[c.farm_name, c.block_name, c.area_name].filter(Boolean).join(' · ')}
                  </span>
                  <span className="text-xs text-red-600">
                    {' '}— {c.severity === 'in_harvest'
                      ? `picking already open from ${formatLocalDateInTz(c.earliest_harvest_date, timezone)}`
                      : `earliest harvest ${formatLocalDateInTz(c.earliest_harvest_date, timezone)}, ${c.days_short} day${c.days_short !== 1 ? 's' : ''} short`}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {spray.whp_override_reason && (
            <div className="mt-3 rounded-md bg-white/70 border border-red-200 px-3 py-2">
              <p className="text-xs font-medium text-neutral-600">
                Reason given by {spray.whp_override_requested_by_name || 'the creator'}
                {spray.whp_override_requested_at && (
                  <> · {formatDateTimeInTz(spray.whp_override_requested_at, timezone)}</>
                )}
              </p>
              <p className="text-sm text-neutral-800 mt-0.5">{spray.whp_override_reason}</p>
            </div>
          )}

          {(status === 'approved' || status === 'rejected') && (
            <div className="mt-3 rounded-md bg-white/70 border border-neutral-200 px-3 py-2">
              <p className="text-xs font-medium text-neutral-600">
                {status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                {spray.whp_override_decided_by_name || 'a Spray Admin'}
                {spray.whp_override_decided_at && (
                  <> · {formatDateTimeInTz(spray.whp_override_decided_at, timezone)}</>
                )}
              </p>
              {spray.whp_override_decision_notes && (
                <p className="text-sm text-neutral-800 mt-0.5">{spray.whp_override_decision_notes}</p>
              )}
            </div>
          )}

          {error && (
            <p className="mt-3 text-sm text-red-700 bg-white/70 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}

          {/* The decision itself */}
          {!licenseLoading && status !== 'approved' && (
            isSprayAdmin ? (
              <div className="mt-3">
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Decision notes <span className="text-neutral-500">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="e.g. confirmed with the packhouse that this block is not picked until the 14th"
                  className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm bg-white"
                />
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => decide('approved')}
                    disabled={deciding !== null}
                  >
                    {deciding === 'approved'
                      ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      : <ShieldCheck className="h-4 w-4 mr-1" />}
                    Approve spray
                  </Button>
                  {status === 'pending' && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => decide('rejected')}
                      disabled={deciding !== null}
                    >
                      {deciding === 'rejected'
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <ShieldX className="h-4 w-4 mr-1" />}
                      Reject
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <p className="mt-3 text-sm text-red-700">
                {status === 'pending'
                  ? 'This spray cannot be assigned or started until someone holding a Spray Admin seat approves it.'
                  : 'Only a Spray Admin seat holder can revisit this decision.'}
              </p>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default WhpOverridePanel;
